import * as React from "react";
import {PrimaryButton} from "office-ui-fabric-react/lib/Button";
import {FilingLocation, dummyLocation, DmsSearch, DmsStorageType, EllipsisOptions} from "../models";
import {Icon} from "office-ui-fabric-react/lib/Icon";
import {IconNames} from "office-ui-fabric-react/lib/Icons";
import LocationItem from "./LocationItem";
import {post} from "../api";
import {Spinner, SpinnerSize} from "office-ui-fabric-react";
import LocationItemTabView from "./LocationItemTabView";
import {BrowseDocumentsContext} from "./components/BrowseDmsDocuments";

export interface ResultsBrowserState {
    expandedParent: FilingLocation;
    expandedResults: FilingLocation[];
    isSaving: boolean;
    docsThatNeedSaved: FilingLocation[];
}

export interface ResultsBrowserProps {
    onChooseLocation: (location: FilingLocation) => void;
    backToTheStart: () => void;
    chooseThisLocationText: string;
    rootParent: FilingLocation;
    rootChildren: FilingLocation[];
    isSearching: boolean;
    localDocs: boolean;
    storageType: string;
    userId: string;
    tenantId: string;
    teamsId: string;
    channelId: string;
    TabView: boolean;
    ShowCheckinPanel: (documentInfo: DmsSearch, share: boolean) => void;
    onExpand: (parent: FilingLocation, children: FilingLocation[]) => void;
    onBack: () => void;
    darkMode: boolean;
    checkoutToChannel: boolean;
    ShowFilingButton: (value: boolean, location: FilingLocation) => void;
    clientCollabLicensed: boolean;
    shouldIncludeCheckedOutFiles: boolean;
    EllipsisOptionOnClick: (documentInfo: DmsSearch, option: EllipsisOptions) => void;
    privateChannel: boolean;
    isLitigationHoldEnabled: boolean;
    isFrictionlessSpExperienceEnabled: boolean;
}

export class ResultsBrowser extends React.Component<ResultsBrowserProps, ResultsBrowserState> {
    context!: React.ContextType<typeof BrowseDocumentsContext>;

    constructor(props: ResultsBrowserProps, context: React.ContextType<typeof BrowseDocumentsContext>) {
        super(props, context);

        this.state = {
            expandedParent: dummyLocation,
            expandedResults: [],
            isSaving: false,
            docsThatNeedSaved: [],
        };
    }

    componentDidUpdate(prevProps: ResultsBrowserProps, _: ResultsBrowserState) {
        if (prevProps.rootParent !== this.props.rootParent) {
            this.browsingStackParent = [];
            this.browsingStackChildren = [];
            this.expandedLocation(this.props.rootParent, this.props.rootChildren);
        }
        if (prevProps.rootChildren !== this.props.rootChildren) {
            this.setState({expandedResults: this.props.rootChildren});
        }
        if (this.props.isSearching) {
            this.props.backToTheStart();
        }
    }

    componentDidMount() {
        this.expandedLocation(this.props.rootParent, this.props.rootChildren);
    }

    browsingStackChildren: FilingLocation[][] = [];
    browsingStackParent: FilingLocation[] = [];
    expandedLocation = (parent: FilingLocation, children: FilingLocation[]) => {
        //if(this.props.addinOption == AddinActionEnum.AttachmentFiling) { // still want to be able to file to folders as we set this to false for attaching files
        //for(var i=0; i < children.length; i++) {
        //children[i].CanFileDirect = true;
        // }
        //}

        if (this.state.expandedParent !== dummyLocation) {
            this.browsingStackChildren.push(this.state.expandedResults);
            this.browsingStackParent.push(this.state.expandedParent);
        }

        const filteredDocs: FilingLocation[] = [];

        for (let i = 0; i < children.length; i++) {
            if (
                (children[i].Metadata["InTeams"] == "Yes (Changed Locally)" || children[i].Metadata["InDMS"] == "No") &&
                children[i].IsFile == true
            ) {
                filteredDocs.push(children[i]);
            }
        }

        this.setState({
            expandedParent: parent,
            expandedResults: children,
            docsThatNeedSaved: filteredDocs,
        });

        this.props.onExpand(parent, children);
    };

    expandedBack = () => {
        if (this.browsingStackParent.length > 0) {
            this.setState(
                {
                    expandedParent: this.browsingStackParent.pop(),
                    expandedResults: this.browsingStackChildren.pop(),
                },
                () => {
                    this.props.onExpand(this.state.expandedParent, this.state.expandedResults);
                    this.context.setParentState(
                        {
                            currentPath: this.state.expandedParent.Url,
                            currentFolder: this.state.expandedParent,
                        },
                        () => this.updateFilterOptions()
                    );
                }
            );
        } else {
            this.context.setParentState(
                {
                    currentPath: this.context.caseInfo ? this.context.caseInfo.LinkedMatterUrl : "",
                    currentFolder: null,
                },
                () => this.updateFilterOptions()
            );
            this.setState({
                expandedParent: null,
                expandedResults: [],
            });
            this.props.backToTheStart();
            if (this.props.onBack) this.props.onBack();
        }
    };

    updateFilterOptions() {
        if (
            this.context.caseInfo.StorageType === DmsStorageType.SharePoint &&
            !this.props.localDocs &&
            !this.props.checkoutToChannel
        ) {
            this.context.getFilterOptions();
        }
    }

    chooseLocation = (location: FilingLocation) => (onComplete: () => void) => {
        this.setState({
            expandedParent: null,
            expandedResults: [],
        });

        this.props.backToTheStart();
        this.props.onChooseLocation(location);
        onComplete();
    };

    onClickLocation = (location: FilingLocation) => () => {
        this.setState({
            expandedParent: null,
            expandedResults: [],
        });

        this.props.backToTheStart();
        this.props.onChooseLocation(location);
    };

    chooseLocationTabView = (location: FilingLocation) => {
        this.props.onChooseLocation(location);
    };

    displayFolderText = () => {
        const folders = this.state.expandedResults.filter((i) => !i.IsFile);

        return folders.length > 0;
    };

    displayFileText = () => {
        const files = this.state.expandedResults.filter((i) => i.IsFile);

        if (files.length > 0) {
            return true;
        } else {
            return false;
        }
    };

    saveAllDocs = async (locations: FilingLocation[]) => {
        this.setState({isSaving: true});

        const updatedLocationsSavedStatus = locations;

        await post("/api/teamsdmsintegration/SaveFileToDms", {
            Files: locations,
            TeamsId: this.props.teamsId,
            ChannelId: this.props.channelId,
            UseServiceAppToken: false,
            UserId: this.props.userId,
            TenantId: this.props.tenantId,
            KeepFileInTeams: true,
            KeepFileCheckedOut: true,
            ReplaceVersion: false,
            LitigationHoldEnabled: this.props.isLitigationHoldEnabled
        });

        for (let i = 0; i < updatedLocationsSavedStatus.length; i++) {
            if (updatedLocationsSavedStatus[i].Metadata["InTeams"] == "Yes (Changed Locally)") {
                updatedLocationsSavedStatus[i].Metadata["InTeams"] = "Yes";
            } else if (updatedLocationsSavedStatus[i].Metadata["InDMS"] == "No") {
                updatedLocationsSavedStatus[i].Metadata["InDMS"] = "Yes";
            }
        }

        this.setState({expandedResults: updatedLocationsSavedStatus, docsThatNeedSaved: [], isSaving: false});
    };

    hasFolders() {
        if (this.state.expandedResults.filter((r) => !r.IsFile).length > 0) {
            return true;
        }
        return false;
    }

    render() {
        const noContentMessage = "(N) is empty.";

        return (
            <div>
                <div className="location-list-container">
                    <br></br>
                    <div className="location-list-title">
                        <Icon
                            iconName={IconNames.ChevronLeft}
                            onClick={this.expandedBack}
                            style={{
                                marginRight: "5px",
                                flex: "0 1 auto",
                                cursor: "pointer",
                                color: this.props.darkMode ? "white" : "black",
                            }}
                            className="location-list-title-button"
                            title="Go back"
                        />
                        <div
                            className="location-list-title-text"
                            style={{flex: "1 1 auto", color: this.props.darkMode ? "white" : "black"}}
                        >
                            {this.state.expandedParent.Name}
                        </div>
                    </div>

                    {!this.props.TabView &&
                        this.state.expandedResults.map(
                            (r) =>
                                !r.IsFile && (
                                    <LocationItem
                                        Location={r}
                                        Expanded={this.expandedLocation}
                                        FileTo={this.chooseLocation(r)}
                                        localDocs={this.props.localDocs}
                                        teamsId={this.props.teamsId}
                                        channelId={this.props.channelId}
                                        storageType={this.props.storageType}
                                        UserId={this.props.userId}
                                        tenantId={this.props.tenantId}
                                        isDarkMode={this.props.darkMode}
                                    />
                                )
                        )}
                    {this.props.TabView && this.state.expandedResults.length > 0 && (
                        <LocationItemTabView
                            Locations={this.state.expandedResults}
                            Expanded={this.expandedLocation}
                            FileTo={this.props.onChooseLocation}
                            loading={false}
                            localDocs={this.props.localDocs}
                            teamsId={this.props.teamsId}
                            channelId={this.props.channelId}
                            storageType={this.props.storageType}
                            UserId={this.props.userId}
                            tenantId={this.props.tenantId}
                            ShowCheckinPanel={this.props.ShowCheckinPanel}
                            darkMode={this.props.darkMode}
                            checkoutToChannel={this.props.checkoutToChannel}
                            ShowFilingButton={this.props.ShowFilingButton}
                            clientCollabLicensed={this.props.clientCollabLicensed}
                            shouldIncludeCheckedOutFiles={this.props.shouldIncludeCheckedOutFiles}
                            isBrowseMode={true}
                            EllipsisOptionOnClick={this.props.EllipsisOptionOnClick}
                            expandedParent={this.state.expandedParent}
                            privateChannel={this.props.privateChannel}
                            isLitigationHoldEnabled={this.props.isLitigationHoldEnabled}
                            isFrictionlessSpExperienceEnabled={this.props.isFrictionlessSpExperienceEnabled}
                        />
                    )}

                    {this.state.expandedParent.CanFileDirect && (
                        <PrimaryButton
                            text={this.props.chooseThisLocationText}
                            onClick={this.onClickLocation(this.state.expandedParent)}
                            style={{width: "98%", marginTop: "5px"}}
                        />
                    )}

                    {!this.props.TabView &&
                        this.state.expandedResults.map(
                            (r) =>
                                r.IsFile && (
                                    <LocationItem
                                        Location={r}
                                        Expanded={this.expandedLocation}
                                        FileTo={this.chooseLocation(r)}
                                        localDocs={this.props.localDocs}
                                        teamsId={this.props.teamsId}
                                        channelId={this.props.channelId}
                                        storageType={this.props.storageType}
                                        UserId={this.props.userId}
                                        tenantId={this.props.tenantId}
                                        isDarkMode={this.props.darkMode}
                                    />
                                )
                        )}

                    {this.state.expandedResults.length === 0 && (
                        <div style={{fontSize: "16px"}}>
                            <br></br>
                            {noContentMessage.replace("(N)", this.state.expandedParent.Name)}
                            <hr style={{height: "20px", visibility: "hidden"}} />
                        </div>
                    )}

                    {this.props.localDocs && <br></br>}
                    {this.props.localDocs &&
                        this.state.docsThatNeedSaved.length > 0 &&
                        !this.state.isSaving &&
                        !this.props.TabView && (
                            <PrimaryButton
                                onClick={() => this.saveAllDocs(this.state.expandedResults)}
                                text={"Save All To DMS"}
                            />
                        )}
                    {this.state.isSaving && (
                        <Spinner
                            className={this.props.darkMode ? "darkMode" : "lightMode"}
                            style={{float: "left"}}
                            size={SpinnerSize.medium}
                        />
                    )}
                </div>
            </div>
        );
    }
}
ResultsBrowser.contextType = BrowseDocumentsContext;

export default ResultsBrowser;
